/**
 * Section Pattern 4 Accordion
 *
 * Features:
 * - Click summary to open/close accordion or redirect
 * - Optional hover open (desktop only)
 * - Smooth open/close animations
 */

document.addEventListener('DOMContentLoaded', () => {
    document
        .querySelectorAll('.section-pattern-4-block')
        .forEach(block => new SectionPattern4Accordion(block));
});

class SectionPattern4Accordion {

    constructor(block) {

        // Prevent double initialization
        if (block.dataset.accordionInitialized) return;

        /** ---------------- CONFIGURABLE VARIABLES ---------------- **/
        this.hoverDelay = 80;                  // Delay before hover opens accordion (ms)
        this.animationDuration = 300;          // Accordion open/close duration (ms)
        this.animationEasing = 'ease-out';     // Accordion animation easing
        /** -------------------------------------------------------- **/

        this.block = block;

        // Read dataset values (fail-safe)
        this.redirectUrl = block.dataset.accordionRedirect || '';
        this.openOnHover = block.dataset.openOnHover === 'true' &&
                           window.matchMedia('(min-width: 769px)').matches;

        // Cache all accordion groups
        this.groups = [...block.querySelectorAll('.foxtrot-accordion-group')];

        // Timer for hover delay
        this.hoverTimer = null;

        this.init();

        // Mark block as initialized
        block.dataset.accordionInitialized = 'true';
    }

    /**
     * Initialize event listeners (delegated)
     */
    init() {
        if (!this.groups.length) return;

        // One listener per group
        this.groups.forEach(group => {
            group.addEventListener('click', this.handleClick.bind(this));
            if (this.openOnHover) {
                group.addEventListener('mouseenter', this.handleHover.bind(this), true);
            }
        });
    }

    /**
     * Click handler for accordion summary
     * @param {MouseEvent} e
     */
    handleClick(e) {
        const summary = e.target.closest('summary');
        if (!summary) return;

        const detail = summary.parentElement;
        const group = detail.parentElement;
        if (!group || !group.matches('.foxtrot-accordion-group')) return;

        // Only redirect if clicked near the right edge (like pseudo icon)
        if (this.redirectUrl) {
            const rect = summary.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const iconWidth = 50; // adjust if your CSS icon is wider
            if (clickX >= rect.width - iconWidth) {
                e.preventDefault();
                e.stopPropagation();
                window.location.assign(this.redirectUrl);
                return;
            }
        }

        e.preventDefault();

        const detailsElements = group.querySelectorAll(':scope > details');
        const isClosing = detail.hasAttribute('open');

        if (!isClosing) {
            detailsElements.forEach(sibling => {
                if (sibling !== detail && sibling.hasAttribute('open')) {
                    this.animateClose(sibling);
                }
            });
            this.animateOpen(detail);
        } else {
            this.animateClose(detail);
        }
    }

    /**
     * Hover handler (desktop only)
     * @param {MouseEvent} e
     */
    handleHover(e) {
        if (!this.openOnHover) return;

        const detail = e.target.closest('details');
        if (!detail) return;

        const group = detail.parentElement;
        if (!group || !group.matches('.foxtrot-accordion-group')) return;

        clearTimeout(this.hoverTimer);

        this.hoverTimer = setTimeout(() => {
            const detailsElements = group.querySelectorAll(':scope > details');

            // Close all other open details
            detailsElements.forEach(sibling => {
                if (sibling !== detail && sibling.hasAttribute('open')) {
                    this.animateClose(sibling);
                }
            });

            if (!detail.hasAttribute('open')) {
                this.animateOpen(detail);
            }

        }, this.hoverDelay);
    }

    /**
     * Animate opening of a detail element
     * @param {HTMLElement} details
     */
    animateOpen(details) {
        if (!details || details.dataset.animating) return;

        details.dataset.animating = 'true';
        details.setAttribute('open', '');

        const summary = details.querySelector('summary');
        if (!summary) return;

        const startHeight = summary.offsetHeight;
        const endHeight = details.scrollHeight;

        const animation = details.animate(
            [
                { height: `${startHeight}px` },
                { height: `${endHeight}px` }
            ],
            {
                duration: this.animationDuration,
                easing: this.animationEasing
            }
        );

        animation.onfinish = () => {
            details.style.height = '';
            details.open = true;
            delete details.dataset.animating;
        };
    }

    /**
     * Animate closing of a detail element
     * @param {HTMLElement} details
     */
    animateClose(details) {
        if (!details || details.dataset.animating) return;

        details.dataset.animating = 'true';

        const summary = details.querySelector('summary');
        if (!summary) return;

        const startHeight = details.offsetHeight;
        const endHeight = summary.offsetHeight;

        const animation = details.animate(
            [
                { height: `${startHeight}px` },
                { height: `${endHeight}px` }
            ],
            {
                duration: this.animationDuration,
                easing: this.animationEasing
            }
        );

        animation.onfinish = () => {
            details.removeAttribute('open');
            details.style.height = '';
            delete details.dataset.animating;
        };
    }

}